import { Component, inject, OnInit } from '@angular/core';
import { AuthService } from 'app/core/auth.service';
import { AppInformationService, BrewersUnitOfWork } from 'app/core/core.module';
import { LoadAdditive } from 'app/core/model/load-additive';
import { RowDblClickEvent } from 'devextreme/ui/data_grid';
import { confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { LoadAdditiveDialogComponent } from '../load-additive-dialog/load-additive-dialog.component';

@Component({
    standalone: false,
    selector: 'app-load-additive-overview-dialog',
    template: `
        <div class="modal-header">
            <h2 class="modal-title">Load Additives</h2>
        </div>
        <div class="modal-body">
            <dx-data-grid #dataGrid keyExpr="id" [dataSource]="loadAdditives" (onRowDblClick)="rowDoubleClick($event)" [height]="500">
                <dxi-column dataField="abbreviation" width="150"></dxi-column>
                <dxi-column dataField="name" width="250"></dxi-column>
                <dxi-column dataField="price" [format]="{ type: 'currency', precision: 2 }" width="100"></dxi-column>
                <dxi-column dataField="loadYards" caption="Quantity" width="100"></dxi-column>
                <dxi-column></dxi-column>

                <dxo-selection mode="multiple"></dxo-selection>

                <div *dxTemplate="let data of 'toolbarButtons'">
                    <button class="btn btn-sm btn-success" (click)="open()">Add</button>
                    <button
                        class="btn btn-sm btn-primary ms-2"
                        (click)="open(dataGrid.instance.getSelectedRowsData()[0])"
                        [disabled]="dataGrid.instance.getSelectedRowsData().length !== 1"
                    >
                        Edit
                    </button>
                    <button
                        class="btn btn-sm btn-danger ms-2"
                        (click)="delete(dataGrid.instance.getSelectedRowsData())"
                        [disabled]="dataGrid.instance.getSelectedRowsData().length === 0"
                    >
                        Delete
                    </button>
                </div>
                <div *dxTemplate="let data of 'toolbarText'">
                    <span class="me-3">
                        <strong>
                            {{ dataGrid.instance.totalCount() + ' items' }}
                            {{ dataGrid.instance.getSelectedRowsData().length > 0 ? dataGrid.instance.getSelectedRowsData().length + ' selected' : '' }}
                        </strong>
                    </span>
                </div>
            </dx-data-grid>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="close()">Close</button>
        </div>
    `,
    styles: [],
})
export class LoadAdditiveOverviewDialogComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    modalService = inject(BsModalService);

    result = new Subject<{ success: boolean }>();

    loadId: number;
    loadAdditives: LoadAdditive[] = [];

    ngOnInit() {
        this.orderAdditives();
    }

    orderAdditives() {
        this.loadAdditives = _(this.loadAdditives)
            .filter((additive) => !additive.deleted)
            .orderBy((additive) => [additive.displayOrder, additive.abbreviation, additive.name])
            .valueOf();
    }

    rowDoubleClick(e: RowDblClickEvent) {
        if (!e.data) return;
        this.open(e.data);
    }

    async open(loadAdditive?: LoadAdditive) {
        const isNew = !loadAdditive;

        const modalRef = this.modalService.show(LoadAdditiveDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                loadAdditive: loadAdditive,
                loadId: this.loadId,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit([result.value]);
                if (isNew) {
                    this.loadAdditives.push(result.value);
                    this.orderAdditives();
                }
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async delete(loadAdditives: LoadAdditive[]) {
        if (!loadAdditives || loadAdditives.length === 0) return;

        const success = await confirm(`Are you sure you want to delete the selected additive${loadAdditives.length > 1 ? 's' : ''}?`, this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        loadAdditives.forEach((a) => {
            (a.deleted = new Date()), (a.deletedBy = this.auth.currentUser.userName);
        });
        await this.uow.commit(loadAdditives);
        this.loadAdditives = this.loadAdditives.filter((l) => !loadAdditives.includes(l));
        this.appInfo.pleaseWaitHide();
    }

    close() {
        this.result.next({ success: true });
    }
}
